import { FC, useState } from "react";
import { Box, Typography } from "@mui/material";
import { LibraryTemplate } from "../templateLibrary.types";
import { cardMeta, isKit } from "../templateLibraryHelpers";
import templateLibraryStyles from "../TemplateLibrary.styles";
import { ArrowRightGlyph, KitGlyph } from "../TemplateLibraryIcons";

const styles = templateLibraryStyles;

/** Text rules drawn on the thumbnail sheet: [width, darker "heading" rule]. */
const SHEET_LINES: [string, boolean][] = [
  ["100%", false],
  ["94%", false],
  ["100%", false],
  ["70%", false],
  ["28%", true],
  ["100%", false],
  ["97%", false],
  ["100%", false],
  ["84%", false],
  ["60%", false],
];

const Lines = ({ count }: { count: number }) => (
  <>
    {SHEET_LINES.slice(0, count).map(([width, strong], i) => (
      <Box
        key={i}
        sx={[styles.thumbLine(width, strong), strong ? { mt: "4px" } : {}]}
      />
    ))}
  </>
);

/**
 * The front sheet's content: the real cover image the browse call already
 * signed, or the drawn title and text rules when there is none (not
 * published with a cover yet, or the short-lived link failed to load).
 */
const SheetContent = ({
  template,
  lines,
}: {
  template: LibraryTemplate;
  lines: number;
}) => {
  const [failed, setFailed] = useState(false);
  if (template.thumbnailUrl && !failed) {
    return (
      <Box
        component="img"
        src={template.thumbnailUrl}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        sx={styles.thumbImage}
      />
    );
  }
  return (
    <>
      <Box sx={styles.thumbTitle}>{template.name}</Box>
      <Lines count={lines} />
    </>
  );
};

/** Miniature of the first page — a fanned stack of three for a kit. */
const Thumbnail = ({ template }: { template: LibraryTemplate }) => {
  if (!isKit(template)) {
    return (
      <Box sx={styles.thumb(false)} aria-hidden="true">
        <Box sx={[styles.thumbSheet, { position: "relative", overflow: "hidden" }]}>
          <SheetContent template={template} lines={SHEET_LINES.length} />
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={styles.thumb(true)}>
      <Box sx={styles.kitSheet(46, 26, 5, 1)} aria-hidden="true">
        <Lines count={8} />
      </Box>
      <Box sx={styles.kitSheet(38, 20, -4, 2)} aria-hidden="true">
        <Lines count={8} />
      </Box>
      <Box
        sx={[styles.kitSheet(30, 14, 0, 3), { overflow: "hidden" }]}
        aria-hidden="true"
      >
        <SheetContent template={template} lines={8} />
      </Box>
      <Box sx={styles.kitBadge}>
        <KitGlyph sx={{ width: 11, height: 11 }} />
        KIT · {template.documentCount} DOCS
      </Box>
    </Box>
  );
};

interface LibraryTemplateCardProps {
  template: LibraryTemplate;
  onOpen: (template: LibraryTemplate) => void;
}

/**
 * One tile in the library grid. The whole tile opens the template's detail
 * page — the "Use" chip is part of the tile, as in the design.
 */
export const LibraryTemplateCard: FC<LibraryTemplateCardProps> = ({
  template,
  onOpen,
}) => (
  <Box
    component="button"
    type="button"
    aria-label={`Open ${template.name}`}
    onClick={() => onOpen(template)}
    sx={styles.card}
  >
    <Thumbnail template={template} />
    <Box sx={styles.cardTags}>
      {template.category && (
        <Box component="span" sx={styles.categoryPill}>
          {template.category.toUpperCase()}
        </Box>
      )}
      {template.featured && (
        <Box component="span" sx={styles.popularPill}>
          POPULAR
        </Box>
      )}
    </Box>
    <Typography component="div" sx={styles.cardName}>
      {template.name}
    </Typography>
    <Typography component="div" sx={styles.cardDesc}>
      {template.shortDescription}
    </Typography>
    <Box sx={styles.cardFoot}>
      <Box component="span" sx={styles.cardMeta}>
        {cardMeta(template)}
      </Box>
      <Box component="span" className="library-use" sx={styles.cardUse}>
        Use
        <ArrowRightGlyph sx={{ width: 12, height: 12 }} />
      </Box>
    </Box>
  </Box>
);

export default LibraryTemplateCard;
